import { SetUniqueWidgetRefreshContext } from 'App';
import Cookies from 'js-cookie';
import type { Dispatch, SetStateAction } from 'react';
import { useContext, useEffect, useState } from 'react';

import useLocation from 'hooks/useLocation';

import routeOffsetMinutes from './config/routeOffsetMinutes.config.js';
import { WidgetVisibilityContext } from './WidgetVisibilityContext';

const isRouteOffsetKey = (value: string | null): value is keyof typeof routeOffsetMinutes =>
  value !== null && value in routeOffsetMinutes;

const getWidgetTimeLabel = (date: Date, compact = false): string =>
  date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    ...(compact ? {} : { second: '2-digit' }),
    hour12: true,
  });

export type WidgetTimeUpdateProps = {
  realtime?: boolean;
  updateIntervalBasis?: number;
  route?: string | null;
  addMargin?: boolean;
  compact?: boolean;
  className?: string;
};

const WidgetTimeUpdate = ({
  realtime = false,
  updateIntervalBasis = 360,
  route = null,
  addMargin = false,
  compact = false,
  className = '',
}: WidgetTimeUpdateProps): JSX.Element => {
  const configuredOffsetMinutes = isRouteOffsetKey(route) ? routeOffsetMinutes[route] : undefined;
  const updateOffsetMinutes = configuredOffsetMinutes || 3;

  const setUwf = useContext(SetUniqueWidgetRefreshContext) as Dispatch<
    SetStateAction<Record<string, string>>
  >;

  const { pollingActive = true } = useContext(WidgetVisibilityContext);
  const { locations } = useLocation();

  const [nextUpdate, setNextUpdate] = useState<Date | null>(null);
  const [currTime, setCurrTime] = useState<Date>(() => new Date());

  const UPDATE_MINUTES = 3;

  const calculateNextTime = (): Date | null => {
    if (!route) {
      return null;
    }

    if (realtime) {
      const now = new Date();

      now.setMinutes(now.getMinutes() + 1);
      now.setSeconds(0);
      now.setMilliseconds(0);

      return now;
    }

    const now = new Date();
    const currentMinutes = now.getUTCHours() * 60 + now.getUTCMinutes() - 300;

    const lastInterval = Math.floor(currentMinutes / updateIntervalBasis) * updateIntervalBasis;

    let nextUpdateMinutes = lastInterval + updateOffsetMinutes;

    if (currentMinutes >= nextUpdateMinutes) {
      nextUpdateMinutes += updateIntervalBasis;
    }

    const nextUpdateTime = new Date(
      now.getTime() + (nextUpdateMinutes - currentMinutes) * 60 * 1000,
    );

    nextUpdateTime.setSeconds(0);
    nextUpdateTime.setMilliseconds(0);

    return nextUpdateTime;
  };

  useEffect(() => {
    if (!pollingActive) {
      return undefined;
    }

    const getUpdateTime = () => {
      const currentTime = new Date();
      const newUpdateTime = calculateNextTime();

      setCurrTime(currentTime);
      setNextUpdate(newUpdateTime);

      if (route !== null && typeof setUwf === 'function' && newUpdateTime) {
        const city = locations?.effectiveLocation?.city ?? Cookies.get('city');
        const state = locations?.effectiveLocation?.state ?? Cookies.get('state');
        const location = `${city}-${state}`;
        const uniqueKey = `${location}-${route}`;

        // Keep the full time for refresh tracking, even when the UI is compact.
        const refreshValue = getWidgetTimeLabel(newUpdateTime);

        setUwf((prev) => {
          if (prev?.[uniqueKey] === refreshValue) {
            return prev;
          }

          return {
            ...(prev || {}),
            [uniqueKey]: refreshValue,
          };
        });
      }
    };

    getUpdateTime();

    const interval = setInterval(getUpdateTime, 1000 * 60 * UPDATE_MINUTES);

    return () => clearInterval(interval);
  }, [pollingActive, route, realtime, updateIntervalBasis, updateOffsetMinutes, setUwf, locations]);

  const timeUpdateContentClassName = compact
    ? [
        'd-flex',
        'flex-row',
        'flex-nowrap',
        'align-items-center',
        'justify-content-start',
        'gap-3',
        'small',
        'lh-sm',
        'px-2',
        'py-1',
      ].join(' ')
    : [
        'd-flex',
        'flex-column',
        'flex-sm-row',
        'align-items-start',
        'align-items-sm-center',
        'justify-content-start',
        'gap-2',
        'gap-sm-4',
        'px-3',
        'py-2',
      ].join(' ');

  const timeItemClassName = compact
    ? 'fusion-widget-time-item d-inline-flex flex-nowrap align-items-baseline gap-1 text-nowrap'
    : 'fusion-widget-time-item d-inline-flex align-items-baseline gap-2 text-nowrap';

  return (
    <div
      className={`fusion-widget-time-update ${className}`.trim()}
      style={{
        flexShrink: 0,
        marginBottom: addMargin ? 10 : undefined,
      }}
    >
      <dl
        className={`
          fusion-widget-time-update-content
          ${timeUpdateContentClassName}
          mb-0
          text-white
          border-bottom
          border-secondary
        `
          .replace(/\s+/g, ' ')
          .trim()}
        aria-label="Weather update status"
      >
        <div className={timeItemClassName}>
          <dt className="fusion-widget-time-label mb-0 fw-semibold">Updated</dt>

          <dd className={`mb-0 ${compact ? '' : 'font-monospace'}`.trim()}>
            <time dateTime={currTime.toISOString()}>{getWidgetTimeLabel(currTime, compact)}</time>
          </dd>
        </div>

        {nextUpdate && (
          <div className={timeItemClassName}>
            <dt className="fusion-widget-time-label mb-0 fw-semibold">Next</dt>

            <dd className={`mb-0 ${compact ? '' : 'font-monospace'}`.trim()}>
              <time dateTime={nextUpdate.toISOString()}>
                {getWidgetTimeLabel(nextUpdate, compact)}
              </time>
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

export default WidgetTimeUpdate;
